import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { attendanceEventsAPI, employeesAPI, devicesAPI, QUERY_KEYS, useDeleteAttendanceEvent } from '@/api';
import { Card, CardContent, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Scan, Camera, Clock3, User, Smartphone, Eye, CheckCircle2, AlertCircle, Trash2, LoaderCircle, CalendarDays, Database, ShieldCheck, MonitorSmartphone, ImageOff, ChevronDown, ChevronUp } from 'lucide-react';
import { extractPaginatedData } from '@/utils/pagination';
import { useAuth } from '@/context/AuthContext';
import { useBranchScope } from '@/context/BranchScopeContext';
import { formatAttendanceDate, formatAttendanceTime } from '../utils/datetime';

type AttendanceEventRecord = {
  id: number;
  employee_id?: number | null;
  device_user_id?: number | string | null;
  occurred_at: string;
  event_type: string | number;
  raw_event_type?: number | string | null;
  event_type_source?: string | null;
  verify_type: string | number;
  photo_url?: string | null;
  raw_payload?: unknown;
  employee?: {
    full_name?: string | null;
    employee_id?: string | null;
  } | null;
  device?: {
    device_name?: string | null;
    device_sn?: string | null;
  } | null;
};

export default function AttendanceEventsPage() {
  const perPage = 20;
  const { hasRole } = useAuth();
  const canDelete = hasRole('super_admin');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    employee_id: '',
    device_id: '',
    date_from: '',
    date_to: '',
  });
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [isJsonOpen, setIsJsonOpen] = useState(false);
  const deleteMutation = useDeleteAttendanceEvent();

  const { branchParam } = useBranchScope();
  const { data: eventsData, isLoading, refetch } = useQuery({
    queryKey: [...QUERY_KEYS.attendanceEvents(), filters, branchParam, page],
    queryFn: () =>
      attendanceEventsAPI.list({ ...filters, branch_id: branchParam, page, per_page: perPage }),
  });

  const { data: employeesData } = useQuery({
    queryKey: QUERY_KEYS.employees(),
    queryFn: () => employeesAPI.list({ per_page: 1000 }),
  });

  const { data: devicesData } = useQuery({
    queryKey: QUERY_KEYS.devices(),
    queryFn: () => devicesAPI.list({ per_page: 100 }),
  });

  const { data: selectedEventData, isFetching: isSelectedEventLoading } = useQuery({
    queryKey: [...QUERY_KEYS.attendanceEvents(), 'detail', selectedEventId],
    queryFn: () => attendanceEventsAPI.get(selectedEventId as number),
    enabled: selectedEventId !== null,
  });

  const { items: events, meta } = extractPaginatedData<AttendanceEventRecord>(eventsData, page, perPage);
  const employees = Array.isArray(employeesData?.data)
    ? employeesData.data
    : employeesData?.data?.data || [];
  const devices = Array.isArray(devicesData?.data)
    ? devicesData.data
    : devicesData?.data?.data || [];
  const selectedEvent = (selectedEventData?.data as AttendanceEventRecord | undefined)
    ?? events.find((event) => event.id === selectedEventId)
    ?? null;

  const openSelectedEvent = (eventId: number) => {
    setSelectedEventId(eventId);
    setIsJsonOpen(false);
  };

  const closeSelectedEvent = () => {
    setSelectedEventId(null);
    setIsJsonOpen(false);
  };

  const handleDelete = async (eventId: number) => {
    const confirmed = window.confirm('Eski attendance eventni o\'chirasizmi?');
    if (!confirmed) {
      return;
    }

    await deleteMutation.mutateAsync(eventId);

    if (selectedEventId === eventId) {
      closeSelectedEvent();
    }

    await refetch();
  };

  const getEventTypeBadge = (type: string | number | null | undefined, source?: string | null) => {
    const normalized = source === 'unscheduled'
      ? 'terminal'
      : typeof type === 'string'
        ? type
        : (Number(type) === 0 ? 'check_out' : 'check_in');
    const colors: Record<string, string> = {
      check_in: 'bg-green-100 text-green-800',
      check_out: 'bg-blue-100 text-blue-800',
      terminal: 'bg-slate-100 text-slate-700',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[normalized] || 'bg-gray-100 text-gray-800'}`}>
        {normalized === 'check_in' ? 'Вход' : normalized === 'check_out' ? 'Выход' : 'Терминал'}
      </span>
    );
  };

  const getEventTypeSubtitle = (type: string | number | null | undefined, rawType?: string | number | null, source?: string | null) => {
    const normalized = source === 'unscheduled'
      ? 'terminal'
      : typeof type === 'string'
        ? type
        : (Number(type) === 0 ? 'check_out' : 'check_in');

    if (normalized === 'terminal') {
      const rawLabel = rawType === null || rawType === undefined
        ? null
        : (Number(rawType) === 0 ? 'raw: выход' : 'raw: вход');

      return rawLabel
        ? `Активной смены нет, ${rawLabel}`
        : 'Активной смены нет';
    }

    return normalized === 'check_out' ? 'Событие выхода' : 'Событие входа';
  };

  const getVerifyTypeBadge = (type: string | number) => {
    const normalized = Number(type);
    const labelMap: Record<number, string> = {
      0: 'Пароль',
      1: 'Карта',
      2: 'Отпечаток',
      3: 'Лицо',
      4: 'Ладонь',
    };
    const colorMap: Record<number, string> = {
      0: 'bg-gray-100 text-gray-700',
      1: 'bg-blue-100 text-blue-700',
      2: 'bg-purple-100 text-purple-700',
      3: 'bg-green-100 text-green-700',
      4: 'bg-orange-100 text-orange-700',
    };
    const icons: Record<number, React.ReactNode> = {
      0: <Clock3 className="w-4 h-4" />,
      1: <Smartphone className="w-4 h-4" />,
      2: <Scan className="w-4 h-4" />,
      3: <Camera className="w-4 h-4" />,
      4: <Scan className="w-4 h-4" />,
    };
    const label = labelMap[normalized] || `Тип ${type}`;
    const color = colorMap[normalized] || 'bg-gray-100 text-gray-700';
    return (
      <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {icons[normalized] || null}
        {label}
      </span>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">События посещаемости</h1>
        <Button variant="outline" onClick={() => refetch()}>
          Обновить
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-gray-500 block mb-1">Сотрудник</label>
              <select
                className="w-full px-4 py-2 border rounded-lg"
                value={filters.employee_id}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, employee_id: e.target.value });
                }}
              >
                <option value="">Все сотрудники</option>
                {employees.map((emp: any) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">Устройство</label>
              <select
                className="w-full px-4 py-2 border rounded-lg"
                value={filters.device_id}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, device_id: e.target.value });
                }}
              >
                <option value="">Все устройства</option>
                {devices.map((device: any) => (
                  <option key={device.id} value={device.id}>
                    {device.device_name || device.device_sn}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">С даты</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, date_from: e.target.value });
                }}
              />
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">По дату</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, date_to: e.target.value });
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={selectedEventId !== null}
        onClose={closeSelectedEvent}
        title="Событие посещаемости"
        maxWidthClass="max-w-4xl"
      >
        {!selectedEvent ? (
          <div className="flex min-h-[18rem] items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(135deg,#eff6ff_0%,#ffffff_50%,#f8fafc_100%)] p-4 shadow-sm sm:p-5">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {getEventTypeBadge(selectedEvent.event_type, selectedEvent.event_type_source)}
                {getVerifyTypeBadge(selectedEvent.verify_type)}
                <Badge variant={selectedEvent.employee_id ? 'success' : 'warning'}>
                  {selectedEvent.employee_id ? 'Связано' : 'Не проверено'}
                </Badge>
                {isSelectedEventLoading && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                    <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                    Обновляется
                  </span>
                )}
              </div>

              <div className={`grid gap-4 ${selectedEvent.photo_url ? 'lg:grid-cols-[240px_minmax(0,1fr)]' : ''}`}>
                {selectedEvent.photo_url ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Фото</p>
                      <Badge variant="outline">Есть</Badge>
                    </div>
                    <EventPhotoPreview
                      eventId={selectedEvent.id}
                      photoUrl={selectedEvent.photo_url}
                      size="lg"
                    />
                  </div>
                ) : null}

                <div className={`grid gap-3 ${selectedEvent.photo_url ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
                  <DetailStat
                    icon={<User className="h-4 w-4" />}
                    label="Сотрудник"
                    value={selectedEvent.employee?.full_name || 'Неизвестный сотрудник'}
                    subtitle={selectedEvent.employee?.employee_id || `ID устройства: ${selectedEvent.device_user_id ?? '-'}`}
                  />
                  <DetailStat
                    icon={<MonitorSmartphone className="h-4 w-4" />}
                    label="Устройство"
                    value={selectedEvent.device?.device_name || selectedEvent.device?.device_sn || '-'}
                    subtitle={selectedEvent.device?.device_sn || 'Серийный номер не указан'}
                  />
                  <DetailStat
                    icon={<CalendarDays className="h-4 w-4" />}
                    label="Дата и время"
                    value={`${formatAttendanceDate(selectedEvent.occurred_at, 'ru-RU') ?? '—'} ${formatAttendanceTime(selectedEvent.occurred_at, 'ru-RU') ?? ''}`.trim()}
                    subtitle={formatAttendanceTime(selectedEvent.occurred_at, 'ru-RU') ?? '—'}
                  />
                  <DetailStat
                    icon={<ShieldCheck className="h-4 w-4" />}
                    label="Верификация"
                    value={verifyTypeLabel(selectedEvent.verify_type)}
                    subtitle={selectedEvent.photo_url ? 'Фото прикреплено' : undefined}
                  />
                  <DetailStat
                    icon={<CheckCircle2 className="h-4 w-4" />}
                    label="Статус"
                    value={selectedEvent.employee_id ? 'Связано с сотрудником' : 'Нужна ручная проверка'}
                    subtitle={getEventTypeSubtitle(selectedEvent.event_type, selectedEvent.raw_event_type, selectedEvent.event_type_source)}
                  />
                  <DetailStat
                    icon={<Database className="h-4 w-4" />}
                    label="Идентификатор устройства"
                    value={String(selectedEvent.device_user_id ?? '-')}
                    subtitle={`ID события: ${selectedEvent.id}`}
                  />
                </div>
              </div>
            </div>

            {selectedEvent.raw_payload ? (
              <Card>
                <CardContent className="p-0">
                  <button
                    type="button"
                    onClick={() => setIsJsonOpen((prev) => !prev)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-slate-50 sm:px-5"
                  >
                    <div>
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <Database className="h-4 w-4 text-slate-500" />
                        JSON от терминала
                      </div>
                      <p className="mt-1 text-xs text-slate-500">Нажмите, чтобы открыть.</p>
                    </div>
                    {isJsonOpen ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </button>
                  {isJsonOpen ? (
                    <pre className="max-h-[18rem] overflow-auto border-t border-slate-200 bg-slate-950 px-4 py-4 text-xs leading-6 text-slate-100 sm:px-5">
                      {JSON.stringify(selectedEvent.raw_payload, null, 2)}
                    </pre>
                  ) : null}
                </CardContent>
              </Card>
            ) : null}

            <div className="flex flex-wrap justify-end gap-3">
              {canDelete && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(selectedEvent.id)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                  Удалить
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={closeSelectedEvent}>
                Закрыть
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Events Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Время</th>
                  <th className="text-left py-3 px-4 font-semibold">Сотрудник</th>
                  <th className="text-left py-3 px-4 font-semibold">Устройство</th>
                  <th className="text-left py-3 px-4 font-semibold">Событие</th>
                  <th className="text-left py-3 px-4 font-semibold">Верификация</th>
                  <th className="text-left py-3 px-4 font-semibold">Статус</th>
                  <th className="text-left py-3 px-4 font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="text-sm">
                        <div className="font-medium">
                          {formatAttendanceTime(event.occurred_at, 'ru-RU') ?? '—'}
                        </div>
                        <div className="text-gray-500">
                          {formatAttendanceDate(event.occurred_at, 'ru-RU') ?? '—'}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        {event.employee?.full_name || (
                          <span className="text-orange-600">Неизвестно (ID: {event.device_user_id})</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm">{event.device?.device_name || event.device?.device_sn || '-'}</span>
                    </td>
                    <td className="py-3 px-4">{getEventTypeBadge(event.event_type, event.event_type_source)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <EventPhotoPreview eventId={event.id} photoUrl={event.photo_url} size="sm" />
                        {getVerifyTypeBadge(event.verify_type)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {event.employee_id ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openSelectedEvent(event.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(event.id)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />

      {events.length === 0 && (
        <div className="text-center py-12">
          <Scan className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">События не найдены</h3>
          <p className="text-gray-500">События от терминалов будут отображаться здесь.</p>
        </div>
      )}
    </div>
  );
}

function DetailStat({
  icon,
  label,
  value,
  subtitle,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/85 p-3.5 shadow-sm backdrop-blur-sm">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-700">
          {icon}
        </span>
        {label}
      </div>
      <p className="text-sm font-semibold text-slate-900 sm:text-base">{value}</p>
      {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
    </div>
  );
}

function EventPhotoPreview({
  eventId,
  photoUrl,
  size = 'sm',
}: {
  eventId?: number | null;
  photoUrl?: string | null;
  size?: 'sm' | 'lg';
}) {
  const { data: photoBlob, isLoading, isError } = useQuery({
    queryKey: [...QUERY_KEYS.attendanceEvents(), 'photo', eventId],
    queryFn: () => attendanceEventsAPI.photo(eventId as number),
    enabled: Boolean(eventId && photoUrl),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const objectUrl = useMemo(() => {
    if (!(photoBlob instanceof Blob)) {
      return null;
    }

    return URL.createObjectURL(photoBlob);
  }, [photoBlob]);

  useEffect(() => {
    if (!objectUrl) {
      return;
    }

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  if (!photoUrl) {
    return null;
  }

  if (isLoading) {
    return <PhotoFallback size={size} message="Yuklanmoqda" loading />;
  }

  if (isError || !objectUrl) {
    return <PhotoFallback size={size} message="Rasm ochilmadi" />;
  }

  const sizeClass = size === 'lg' ? 'h-56 w-full sm:h-64' : 'h-12 w-12';

  return (
    <img
      src={objectUrl}
      alt="Event photo"
      loading="lazy"
      className={`${sizeClass} rounded-2xl border border-slate-200 object-cover bg-slate-50 shadow-sm`}
    />
  );
}

function PhotoFallback({ size, message, loading = false }: { size: 'sm' | 'lg'; message: string; loading?: boolean }) {
  const wrapperClass = size === 'lg'
    ? 'h-72 w-full sm:h-80 rounded-2xl'
    : 'h-12 w-12 rounded-xl';

  return (
    <div className={`${wrapperClass} flex items-center justify-center border border-dashed border-slate-300 bg-slate-50 text-slate-400`}>
      {size === 'lg' ? (
        <div className="flex flex-col items-center gap-2 text-center">
          {loading ? <LoaderCircle className="h-7 w-7 animate-spin" /> : <ImageOff className="h-7 w-7" />}
          <span className="text-sm font-medium text-slate-500">{message}</span>
        </div>
      ) : loading ? (
        <LoaderCircle className="h-4 w-4 animate-spin" />
      ) : (
        <Camera className="h-4 w-4" />
      )}
    </div>
  );
}

function verifyTypeLabel(type: string | number): string {
  const normalized = Number(type);
  const labelMap: Record<number, string> = {
    0: 'Пароль',
    1: 'Карта',
    2: 'Отпечаток',
    3: 'Лицо',
    4: 'Ладонь',
  };

  return labelMap[normalized] || `Тип ${type}`;
}
